/**
 * Header JavaScript
 * DVT Advies Theme
 *
 * Handles:
 * - Desktop mega menu interactions
 * - Mobile menu toggle
 * - Mobile submenu navigation
 */

var DVT_DESKTOP_BREAKPOINT = 1024;

function isDesktopCategoryClickThrough() {
    return window.innerWidth > DVT_DESKTOP_BREAKPOINT
        && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
        initMegaMenu();
        initMobileMenu();
    });
}

if (document.readyState !== 'loading') {
    initMegaMenu();
    initMobileMenu();
}

/**
 * Desktop Mega Menu
 */
function initMegaMenu() {
    const menuItems = document.querySelectorAll('.js-has-mega-menu');

    if (!menuItems.length) return;

    menuItems.forEach(item => {
        const megaMenu = item.querySelector('.c-mega-menu');
        const megaMenuContainer = megaMenu?.querySelector('.c-mega-menu__container');
        const link = item.querySelector('.c-header__menu-link');
        const categoryBtns = item.querySelectorAll('.js-category-btn');
        const contentPanels = item.querySelectorAll('.c-mega-menu__content');

        if (!megaMenu) return;

        // Set first category as active by default
        if (categoryBtns.length > 0) {
            categoryBtns[0].closest('.c-mega-menu__category-item').classList.add('is-active');
        }
        if (contentPanels.length > 0) {
            contentPanels[0].classList.add('is-active');
        }

        function activateCategory(btn) {
            const categoryId = btn.dataset.category;
            const categoryItem = btn.closest('.c-mega-menu__category-item');

            item.querySelectorAll('.c-mega-menu__category-item').forEach(cat => {
                cat.classList.remove('is-active');
            });

            contentPanels.forEach(panel => {
                panel.classList.remove('is-active');
            });

            if (categoryItem) {
                categoryItem.classList.add('is-active');
            }

            const targetPanel = item.querySelector(`.c-mega-menu__content[data-category="${categoryId}"]`);
            if (targetPanel) {
                targetPanel.classList.add('is-active');
            }
        }

        categoryBtns.forEach(btn => {
            btn.addEventListener('click', function(event) {
                if (!isDesktopCategoryClickThrough()) {
                    event.preventDefault();
                    activateCategory(this);
                    return;
                }

                activateCategory(this);
            });

            btn.addEventListener('mouseenter', function() {
                activateCategory(this);
            });
        });

        // Handle hover — listeners target the link and the container specifically,
        // NOT the <li> or the full-width mega menu wrapper. This prevents the
        // dropdown from staying open when hovering the empty space beside it.
        let closeTimeout;

        function openMega() {
            clearTimeout(closeTimeout);
            // Close other open mega menus first
            menuItems.forEach(other => {
                if (other !== item) other.classList.remove('is-active');
            });
            item.classList.add('is-active');
        }

        function closeMega() {
            closeTimeout = setTimeout(() => {
                item.classList.remove('is-active');
            }, 200);
        }

        // Open/close on the link element (the visible nav text)
        if (link) {
            link.addEventListener('mouseenter', openMega);
            link.addEventListener('mouseleave', closeMega);
            link.addEventListener('focus', openMega);
        }

        // Keep open when hovering the actual dropdown container (not the full-width wrapper)
        if (megaMenuContainer) {
            megaMenuContainer.addEventListener('mouseenter', openMega);
            megaMenuContainer.addEventListener('mouseleave', closeMega);
        }
    });

    // Close all mega menus on escape key (single listener)
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
            menuItems.forEach(item => {
                item.classList.remove('is-active');
            });
        }
    });

    // Close mega menu when clicking outside
    document.addEventListener('click', function(e) {
        if (!e.target.closest('.js-has-mega-menu') && !e.target.closest('.c-mega-menu')) {
            menuItems.forEach(item => {
                item.classList.remove('is-active');
            });
        }
    });
}

/**
 * Mobile Menu
 */
function initMobileMenu() {
    const menuToggle = document.querySelector('.js-menu-toggle');
    const menuClose = document.querySelector('.js-menu-close');
    const mobileMenu = document.querySelector('.js-mobile-menu');
    const overlay = document.querySelector('.js-mobile-menu-overlay');
    const submenuTriggers = document.querySelectorAll('.js-mobile-submenu-trigger');
    const submenuContainer = document.querySelector('.js-mobile-submenu');
    const mainNav = document.querySelector('.js-mobile-nav-main');
    const menuContainer = mobileMenu?.querySelector('.c-mobile-menu__container');

    if (!mobileMenu) return;
    
    // Toggle menu
    function openMenu() {
        mobileMenu.classList.add('is-active');
        mobileMenu.setAttribute('aria-hidden', 'false');
        document.body.classList.add('no-scroll');
        menuToggle?.classList.add('is-active');
        menuToggle?.setAttribute('aria-expanded', 'true');
        
        // Adjust height after menu is shown
        adjustContainerHeight();
    }

    function closeMenu() {
        mobileMenu.classList.remove('is-active');
        mobileMenu.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('no-scroll');
        menuToggle?.classList.remove('is-active');
        menuToggle?.setAttribute('aria-expanded', 'false');

        // Also close any open submenu
        closeSubmenu();
    }

    // Helper function to adjust container height
    function adjustContainerHeight() {
        if (!menuContainer) return;

        const getElementHeight = (element, useScrollHeight = false) => {
            if (!element) return 0;
            const style = getComputedStyle(element);
            const marginTop = parseFloat(style.marginTop) || 0;
            const marginBottom = parseFloat(style.marginBottom) || 0;
            const baseHeight = useScrollHeight ? element.scrollHeight : element.offsetHeight;
            return baseHeight + marginTop + marginBottom;
        };

        // Wait for DOM to be fully rendered
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const isSubmenuActive = submenuContainer?.classList.contains('is-active');
                const cssMaxHeight = parseFloat(getComputedStyle(menuContainer).maxHeight);
                const viewportHeight = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
                const viewportCap = viewportHeight - 40;
                const maxHeight = Math.min(
                    Number.isFinite(cssMaxHeight) ? cssMaxHeight : viewportCap,
                    viewportCap
                );

                if (isSubmenuActive && submenuContainer) {
                    const submenuHeader = submenuContainer.querySelector('.c-mobile-menu__header');
                    const submenuHeaderSection = submenuContainer.querySelector('.c-mobile-menu__submenu-header');
                    const submenuContent = submenuContainer.querySelector('.c-mobile-menu__submenu-content');

                    let submenuHeight = 0;
                    submenuHeight += getElementHeight(submenuHeader);
                    submenuHeight += getElementHeight(submenuHeaderSection);
                    submenuHeight += getElementHeight(submenuContent, true);

                    const finalHeight = Math.min(submenuHeight, maxHeight);
                    menuContainer.style.height = `${finalHeight}px`;
                }

                if (!isSubmenuActive) {
                    const mainHeader = menuContainer.querySelector('.c-mobile-menu__header');
                    const mainNav = menuContainer.querySelector('.c-mobile-menu__nav');
                    const mainNavList = mainNav?.querySelector('.c-mobile-menu__list');
                    const mainCta = menuContainer.querySelector('.c-mobile-menu__cta');

                    let mainMenuHeight = 0;
                    mainMenuHeight += getElementHeight(mainHeader);

                    if (mainNav) {
                        const navStyle = getComputedStyle(mainNav);
                        const navPaddingTop = parseFloat(navStyle.paddingTop) || 0;
                        const navPaddingBottom = parseFloat(navStyle.paddingBottom) || 0;
                        const navContentHeight = mainNavList ? mainNavList.scrollHeight : mainNav.scrollHeight;
                        mainMenuHeight += navContentHeight + navPaddingTop + navPaddingBottom;
                    }

                    mainMenuHeight += getElementHeight(mainCta);

                    const finalHeight = Math.min(mainMenuHeight, maxHeight);
                    menuContainer.style.height = `${finalHeight}px`;
                }
            });
        });
    }

    // Submenu navigation
    function openSubmenu(title, menuItemId, url) {
        if (!submenuContainer) return;

        // Get the submenu content from the main nav
        const menuItem = document.querySelector(`[data-submenu="${menuItemId}"]`)?.closest('.c-mobile-menu__item');
        const submenuList = menuItem?.querySelector('.c-mobile-menu__submenu-list');

        if (!submenuList) return;

        // Build submenu DOM
        const submenuFragment = buildSubmenuFragment(title, submenuList, menuItemId, url);
        if (!submenuFragment) return;
        submenuContainer.innerHTML = '';
        submenuContainer.appendChild(submenuFragment);

        // Show submenu
        submenuContainer.classList.add('is-active');
        submenuContainer.setAttribute('aria-hidden', 'false');
        mainNav?.setAttribute('aria-hidden', 'true');

        // Initialize submenu tabs
        initSubmenuTabs();

        // Initialize back button
        const backBtn = submenuContainer.querySelector('.c-mobile-menu__submenu-back');
        if (backBtn) {
            backBtn.addEventListener('click', closeSubmenu);
        }

        // Adjust container height after submenu is shown
        adjustContainerHeight();
    }

    function closeSubmenu() {
        if (!submenuContainer) return;

        submenuContainer.classList.remove('is-active');
        submenuContainer.setAttribute('aria-hidden', 'true');
        mainNav?.setAttribute('aria-hidden', 'false');

        // Reset container height to auto
        adjustContainerHeight();
    }

    function buildSubmenuFragment(title, submenuList, menuItemId, url) {
        const baseFragment = cloneTemplateFragment('mobile-submenu-template');
        if (!baseFragment) return null;

        const titleEl = baseFragment.querySelector('[data-submenu-title]');
        if (titleEl) {
            if (url) {
                const titleLink = document.createElement('a');
                titleLink.href = url;
                titleLink.className = 'c-mobile-menu__submenu-title-link';
                titleLink.textContent = title;
                titleEl.appendChild(titleLink);
            }

            if (!url) {
                titleEl.textContent = title;
            }
        }

        const contentEl = baseFragment.querySelector('[data-submenu-content]');
        const tabsEl = baseFragment.querySelector('[data-submenu-tabs]');

        if (!contentEl) return null;

        // Get all level-1 items in the submenu
        const items = submenuList.querySelectorAll(':scope > .c-mobile-menu__item');

        // Check if this is a simple (flat) list or a categorized list
        const hasCategories = submenuList.querySelector('.c-mobile-menu__item--has-children');

        if (!hasCategories) {
            if (tabsEl) {
                tabsEl.hidden = true;
            }
            const simpleContent = buildSimpleSubmenuContent(items);
            if (simpleContent) {
                contentEl.appendChild(simpleContent);
            }
            return baseFragment;
        }

        if (tabsEl) {
            tabsEl.hidden = false;
        }
        const categorizedContent = buildCategorizedSubmenuContent(items, menuItemId, tabsEl);
        if (categorizedContent) {
            contentEl.appendChild(categorizedContent);
        }
        return baseFragment;
    }

    function buildSimpleSubmenuContent(items) {
        // Separate regular items from "view all" items
        const regularItems = [];
        let viewAllItem = null;

        items.forEach(item => {
            if (item.classList.contains('c-mobile-menu__item--view-all')) {
                viewAllItem = item;
                return;
            }
            regularItems.push(item);
        });

        // Check if any items have icons or are plain links
        let hasIcons = false;
        regularItems.forEach(item => {
            if (item.querySelector('.c-mobile-menu__icon img') || item.querySelector('.c-mobile-menu__link--with-icon')) {
                hasIcons = true;
            }
        });

        if (hasIcons) {
            const itemsWrapper = cloneTemplateElement('mobile-submenu-items-wrapper-template');
            const itemsList = itemsWrapper?.querySelector('[data-submenu-items]');
            if (!itemsWrapper || !itemsList) return null;

            regularItems.forEach(item => {
                const sourceLink = item.querySelector('.c-mobile-menu__link');
                const builtItem = buildSubmenuItem(sourceLink);
                if (builtItem) {
                    itemsList.appendChild(builtItem);
                }
            });

            if (viewAllItem) {
                const viewAllEl = buildViewAllItem(viewAllItem);
                if (viewAllEl) {
                    itemsList.appendChild(viewAllEl);
                }
            }

            return itemsWrapper;
        }

        const linksWrapper = cloneTemplateElement('mobile-submenu-links-wrapper-template');
        const linksList = linksWrapper?.querySelector('[data-submenu-links]');
        if (!linksWrapper || !linksList) return null;

        regularItems.forEach(item => {
            const link = item.querySelector('.c-mobile-menu__link');
            const linkItem = buildLinkItem(link);
            if (linkItem) {
                linksList.appendChild(linkItem);
            }
        });

        if (viewAllItem) {
            const viewAllEl = buildViewAllItem(viewAllItem);
            if (viewAllEl) {
                linksList.appendChild(viewAllEl);
            }
        }

        return linksWrapper;
    }

    function buildCategorizedSubmenuContent(categories, menuItemId, tabsEl) {
        const contentFragment = document.createDocumentFragment();

        categories.forEach((category, index) => {
            const categoryLink = category.querySelector(':scope > .c-mobile-menu__link, :scope > button');
            const categoryTitle = categoryLink?.querySelector('.c-mobile-menu__link-text')?.textContent || categoryLink?.textContent || '';
            const categoryId = `cat-${menuItemId}-${index}`;
            const isActive = index === 0;

            const categoryUrl = categoryLink?.getAttribute('href') || '';
            const isPlaceholderUrl = !categoryUrl || categoryUrl === '#' || categoryUrl.endsWith('/#');
            const tab = buildTabItem(categoryTitle, categoryId, isActive);
            if (tab && tabsEl) {
                tabsEl.appendChild(tab);
            }

            const categoryPanel = buildCategoryPanel(categoryTitle, categoryId, isActive, isPlaceholderUrl ? '' : categoryUrl);
            if (!categoryPanel) return;

            const nestedList = category.querySelector('.c-mobile-menu__submenu-list, .c-mobile-menu__nested-list');
            const itemsList = categoryPanel.querySelector('[data-category-items]');

            if (nestedList && itemsList) {
                const nestedItems = nestedList.querySelectorAll(':scope > .c-mobile-menu__item');

                nestedItems.forEach(nestedItem => {
                    if (nestedItem.classList.contains('c-mobile-menu__item--view-all')) {
                        const viewAllEl = buildViewAllItem(nestedItem);
                        if (viewAllEl) {
                            itemsList.appendChild(viewAllEl);
                        }
                        return;
                    }
                    if (nestedItem.classList.contains('c-mobile-menu__item--subgroup')) {
                        const heading = buildSubgroupHeading(nestedItem);
                        if (heading) {
                            itemsList.appendChild(heading);
                        }
                        return;
                    }
                    const sourceLink = nestedItem.querySelector('.c-mobile-menu__link');
                    const builtItem = buildSubmenuItem(sourceLink);
                    if (builtItem) {
                        itemsList.appendChild(builtItem);
                    }
                });
            }

            contentFragment.appendChild(categoryPanel);
        });

        return contentFragment;
    }

    function buildSubmenuItem(sourceLink) {
        const submenuItem = cloneTemplateElement('mobile-submenu-item-template');
        if (!submenuItem) return null;

        const href = sourceLink?.href || '#';
        const titleEl = sourceLink?.querySelector('.c-mobile-menu__link-text');
        const descEl = sourceLink?.querySelector('.c-mobile-menu__link-description');
        const iconEl = sourceLink?.querySelector('.c-mobile-menu__icon img');
        const sourceIconContainer = sourceLink?.querySelector('.c-mobile-menu__icon');
        const iconDisplay = sourceIconContainer?.dataset?.iconDisplay || '';

        const itemTitle = titleEl?.textContent || '';
        const itemDesc = descEl?.textContent || '';
        const iconSrc = iconEl?.src || '';

        const link = submenuItem.querySelector('.c-mobile-menu__submenu-item-link');
        if (link) {
            link.href = href;
        }

        const titleTarget = submenuItem.querySelector('[data-item-title]');
        if (titleTarget) {
            titleTarget.textContent = itemTitle;
        }

        const descTarget = submenuItem.querySelector('[data-item-description]');
        if (descTarget) {
            if (itemDesc) {
                descTarget.textContent = itemDesc;
                descTarget.hidden = false;
            }

            if (!itemDesc) {
                descTarget.hidden = true;
            }
        }

        const iconImg = submenuItem.querySelector('[data-icon-image]');
        const iconFallback = submenuItem.querySelector('[data-icon-fallback]');
        const iconContainer = submenuItem.querySelector('.c-mobile-menu__submenu-icon');
        if (iconSrc && iconImg) {
            iconImg.src = iconSrc;
            iconImg.hidden = false;
            if (iconFallback) {
                iconFallback.hidden = true;
            }
            if (iconContainer && iconDisplay === 'image_only') {
                iconContainer.classList.add('c-mobile-menu__submenu-icon--no-background');
            }
        }

        if (!iconSrc || !iconImg) {
            if (iconImg) {
                iconImg.hidden = true;
            }
            if (iconFallback) {
                iconFallback.hidden = false;
            }
        }

        return submenuItem;
    }

    function buildLinkItem(link) {
        const linkItem = cloneTemplateElement('mobile-submenu-link-item-template');
        if (!linkItem) return null;

        const href = link?.href || '#';
        const linkText = link?.querySelector('.c-mobile-menu__link-text')?.textContent || link?.textContent?.trim() || '';

        const anchor = linkItem.querySelector('.c-mobile-menu__submenu-link');
        if (anchor) {
            anchor.href = href;
            anchor.textContent = linkText;
        }

        return linkItem;
    }

    function buildViewAllItem(viewAllDataItem) {
        const viewAllEl = cloneTemplateElement('mobile-submenu-view-all-template');
        if (!viewAllEl) return null;

        const sourceLink = viewAllDataItem.querySelector('.c-mobile-menu__link--view-all');
        const href = sourceLink?.href || '#';

        const link = viewAllEl.querySelector('[data-view-all-link]');
        if (link) {
            link.href = href;
        }

        return viewAllEl;
    }

    function buildTabItem(title, categoryId, isActive) {
        const tab = cloneTemplateElement('mobile-submenu-tab-template');
        if (!tab) return null;

        tab.dataset.category = categoryId;
        if (isActive) {
            tab.classList.add('is-active');
        }

        const text = tab.querySelector('[data-tab-text]');
        if (text) {
            text.textContent = title;
        }

        return tab;
    }

    function buildCategoryPanel(title, categoryId, isActive, url) {
        const panel = cloneTemplateElement('mobile-submenu-category-template');
        if (!panel) return null;

        panel.dataset.category = categoryId;
        if (isActive) {
            panel.classList.add('is-active');
        }

        const titleEl = panel.querySelector('[data-category-title]');
        if (titleEl) {
            if (url) {
                const titleLink = document.createElement('a');
                titleLink.href = url;
                titleLink.className = 'c-mobile-menu__submenu-category-title-link';
                titleLink.textContent = title;
                titleEl.appendChild(titleLink);
            }

            if (!url) {
                titleEl.textContent = title;
            }
        }

        return panel;
    }

    function buildSubgroupHeading(item) {
        const li = document.createElement('li');
        li.className = 'c-mobile-menu__submenu-item c-mobile-menu__submenu-item--subgroup';

        const source = item.querySelector('a');
        const heading = document.createElement('a');
        heading.href = source?.getAttribute('href') || '#';
        heading.className = 'c-mobile-menu__submenu-subgroup-title';
        heading.textContent = source?.textContent?.trim() || '';
        li.appendChild(heading);

        return li;
    }

    function cloneTemplateFragment(id) {
        const template = document.getElementById(id);
        return template?.content ? template.content.cloneNode(true) : null;
    }

    function cloneTemplateElement(id) {
        const template = document.getElementById(id);
        return template?.content?.firstElementChild ? template.content.firstElementChild.cloneNode(true) : null;
    }

    function initSubmenuTabs() {
        const tabs = submenuContainer?.querySelectorAll('.c-mobile-menu__submenu-tab');
        const categories = submenuContainer?.querySelectorAll('.c-mobile-menu__submenu-category');

        tabs?.forEach(tab => {
            tab.addEventListener('click', function() {
                const categoryId = this.dataset.category;

                // Remove active from all tabs and categories
                tabs.forEach(t => t.classList.remove('is-active'));
                categories?.forEach(c => c.classList.remove('is-active'));

                // Add active to clicked tab and corresponding category
                this.classList.add('is-active');
                submenuContainer?.querySelector(`.c-mobile-menu__submenu-category[data-category="${categoryId}"]`)?.classList.add('is-active');

                // Adjust container height after tab switch
                adjustContainerHeight();
            });
        });

        // Close button in submenu
        const closeBtn = submenuContainer?.querySelector('.js-submenu-close');
        closeBtn?.addEventListener('click', closeMenu);
    }

    // Event listeners
    menuToggle?.addEventListener('click', function() {
        if (mobileMenu.classList.contains('is-active')) {
            closeMenu();
            return;
        }

        openMenu();
    });

    menuClose?.addEventListener('click', closeMenu);
    overlay?.addEventListener('click', closeMenu);

    // Submenu triggers
    submenuTriggers.forEach(trigger => {
        trigger.addEventListener('click', function() {
            const title = this.dataset.title || 'Menu';
            const menuItemId = this.dataset.submenu;
            const url = this.dataset.url || '';
            openSubmenu(title, menuItemId, url);
        });
    });

    // Close on escape key
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && mobileMenu.classList.contains('is-active')) {
            closeMenu();
        }
    });

    // Handle resize - close menu on desktop
    let resizeTimer;
    window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function() {
            if (window.innerWidth > DVT_DESKTOP_BREAKPOINT && mobileMenu.classList.contains('is-active')) {
                closeMenu();
            }
        }, 100);
    });
}
